import { Component } from '@theme/component';

const colors = { gray: '#999b9e', 'dark gray': '#505257', black: '#111111', blue: '#2257c6', white: '#ffffff', 'rose red': '#d74376', 'light gray': '#c6c8cc', 'navy blue': '#172a48', 'all black': '#111111', 'off white': '#e8dfcc', red: '#e45c70', navy: '#172a48', 'dark blue': '#172a48', 'army green': '#7a843d', mint: '#4bb97d', slate: '#62676b' };
let pickerId = 0;

/** Presentation only: original Rapid selects continue to own variants and discounts. */
class FiveToeBundleControls extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.scope = this.closest('.product-details') || this.closest('.product-information');
    if (!this.scope || !('showPopover' in HTMLElement.prototype)) return;
    this.controls = new Map();
    this.abort = new AbortController();
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.scope, { childList: true, subtree: true });
    this.scope.addEventListener('change', () => this.schedule(), { signal: this.abort.signal });
    this.enhance();
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = 0; this.enhance(); });
  }

  enhance() {
    if (!this.controls) return;
    for (const [select, control] of this.controls) {
      if (!select.isConnected || !control.root.isConnected) {
        control.root.remove();
        control.menu.remove();
        control.abort.abort();
        select.classList.remove('five-picker-native');
        this.controls.delete(select);
      }
    }
    for (const select of this.scope.querySelectorAll('select.rapi-bundles__bar-variant-select')) {
      if (!['Color', 'Size (US)', 'Size'].includes(select.dataset.variantname)) continue;
      let control = this.controls.get(select);
      if (!control) {
        control = this.createPicker(select);
        this.controls.set(select, control);
      }
      const signature = JSON.stringify([select.value, select.disabled, [...select.options].map(o => [o.value, o.textContent, o.disabled])]);
      if (control.signature !== signature) {
        control.signature = signature;
        this.updatePicker(select, control);
      }
    }
  }

  createPicker(select) {
    const isColor = select.dataset.variantname === 'Color';
    const root = document.createElement('div');
    root.className = `five-picker five-picker--${isColor ? 'color' : 'size'}`;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'five-picker__trigger';
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    const label = document.createElement('span');
    label.className = 'five-picker__label';
    label.textContent = isColor ? 'Color' : 'Size';
    const value = document.createElement('span');
    value.className = 'five-picker__value';
    button.append(value);
    root.append(label, button);
    select.insertAdjacentElement('afterend', root);
    if (!isColor) select.parentElement.prepend(root);
    const menu = document.createElement('div');
    menu.className = `five-picker__menu five-picker__menu--${isColor ? 'color' : 'size'}`;
    menu.id = `five-picker-menu-${++pickerId}`;
    menu.setAttribute('popover', 'auto');
    menu.setAttribute('role', 'listbox');
    menu.setAttribute('aria-label', isColor ? 'Choose color' : `Choose ${select.dataset.variantname.toLowerCase()}`);
    button.setAttribute('aria-controls', menu.id);
    document.body.append(menu);
    select.classList.add('five-picker-native');
    const abort = new AbortController();
    const opts = { signal: abort.signal };
    const control = { root, button, value, menu, abort, isColor };
    const close = () => { if (menu.matches(':popover-open')) menu.hidePopover(); };
    const open = () => {
      const rect = button.getBoundingClientRect();
      const width = Math.min(isColor ? Math.max(rect.width, 210) : 310, window.innerWidth - 24);
      menu.style.width = `${width}px`;
      menu.style.left = `${Math.max(12, Math.min(rect.left, window.innerWidth - width - 12))}px`;
      const roomBelow = window.innerHeight - rect.bottom - 12;
      const below = roomBelow >= 190 || rect.top < roomBelow;
      menu.style.top = below ? `${rect.bottom + 6}px` : 'auto';
      menu.style.bottom = below ? 'auto' : `${window.innerHeight - rect.top + 6}px`;
      menu.style.maxHeight = `${Math.min(300, Math.max(120, below ? roomBelow - 6 : rect.top - 18))}px`;
      menu.showPopover();
      menu.querySelector('[aria-selected="true"]')?.focus({ preventScroll: true });
    };
    button.addEventListener('click', () => menu.matches(':popover-open') ? close() : open(), opts);
    button.addEventListener('keydown', event => {
      if (['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); open(); }
    }, opts);
    menu.addEventListener('toggle', () => button.setAttribute('aria-expanded', String(menu.matches(':popover-open'))), opts);
    menu.addEventListener('click', event => {
      const option = event.target.closest('button[data-value]');
      if (!option || option.disabled) return;
      select.value = option.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.updatePicker(select, control);
      close();
      button.focus({ preventScroll: true });
    }, opts);
    menu.addEventListener('keydown', event => {
      const items = [...menu.querySelectorAll('button:not(:disabled)')];
      const index = items.indexOf(document.activeElement);
      if (event.key === 'Escape') { event.preventDefault(); close(); button.focus({ preventScroll: true }); return; }
      if (event.key === 'Tab') { close(); button.focus({ preventScroll: true }); return; }
      const next = { ArrowDown: (index + 1) % items.length, ArrowUp: (index - 1 + items.length) % items.length, Home: 0, End: items.length - 1 }[event.key];
      if (next !== undefined) { event.preventDefault(); items[next]?.focus(); }
    }, opts);
    window.addEventListener('resize', close, opts);
    window.addEventListener('scroll', event => { if (!menu.contains(event.target)) close(); }, { ...opts, capture: true, passive: true });
    return control;
  }

  setLabel(element, text, isColor, compact = false) {
    element.replaceChildren();
    if (isColor) {
      const swatch = document.createElement('span');
      swatch.className = 'five-picker__swatch';
      swatch.style.background = colors[text.trim().toLowerCase()] || '#999999';
      swatch.setAttribute('aria-hidden', 'true');
      element.append(swatch);
    }
    const label = document.createElement('span');
    label.textContent = compact && !isColor ? text.split(' · ')[0] : text;
    element.append(label);
  }

  updatePicker(select, control) {
    const text = select.selectedOptions[0]?.textContent || '';
    control.root.classList.toggle('five-picker--us-shoe', !control.isColor && text.trim().startsWith('US M'));
    control.root.classList.toggle('five-picker--one-size', !control.isColor && text.trim().toLowerCase() === 'one size');
    this.setLabel(control.value, text, control.isColor, true);
    control.button.setAttribute('aria-label', `${control.isColor ? 'Color' : 'Size'}: ${text}`);
    control.button.disabled = select.disabled;
    control.menu.replaceChildren();
    for (const option of select.options) {
      const item = document.createElement('button');
      item.type = 'button';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', String(option.selected));
      item.tabIndex = option.selected ? 0 : -1;
      item.dataset.value = option.value;
      item.disabled = option.disabled;
      this.setLabel(item, option.textContent, control.isColor);
      control.menu.append(item);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.abort?.abort();
    cancelAnimationFrame(this.frame);
    for (const [select, control] of this.controls || []) {
      control.abort.abort(); control.root.remove(); control.menu.remove();
      select.classList.remove('five-picker-native');
    }
    this.controls?.clear();
    super.disconnectedCallback();
  }
}
if (!customElements.get('five-toe-bundle-controls')) customElements.define('five-toe-bundle-controls', FiveToeBundleControls);
